import * as _ from 'lodash';
import { useCallback } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../../contexts/RouterContext';
import Drawer from '../../../generic/Drawer';
import FallbackImage from '../../../generic/FallbackImage';
import Tabs, { useTabs } from '../../../generic/Tabs';
import { useSnippetById } from '../hooks';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';

import Buttons from './Buttons';
import Code from './Code';
import Details from './Details';
import Header from './Header';
import Loading from './Loading';

type DrawerTab = 'Details' | 'Compare Code';

const tabIds: Readonly<{ tabIds: DrawerTab[] }> = {
  tabIds: ['Details', 'Compare Code'],
};

type Props = {
  /**
   * This is required to either fetch or look up the snippet info from ReactQuery
   */
  userRevisionLocator: string;
};

/**
 * Drawer which appears when a snippet has been clicked.
 * Contains a header with some info, and tabs for switching between
 * the "details" view and the code view.
 */
const SnippetsDrawer: VoidFunctionComponent<Props> = ({ userRevisionLocator }) => {
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const { tabs, activeTab } = useTabs<DrawerTab>(tabIds);

  //  Unset the queryParam.drawerId to close it
  const onClose = useCallback(() => {
    setQuery({ ...queryParams, selectedSnippetId: undefined });
  }, [queryParams, setQuery]);

  //  We don't have an endpoint for serving just a single snippet,
  //  so in the drawer we also "fetch" the list of snippets and grab the one which matches the selectedSnippetId
  const { snippet, isLoading } = useSnippetById(userRevisionLocator, queryParams.selectedSnippetId);

  //  If there is no snippet to display (if it isn't in the list of `snippets`), display a fallback UI
  if (!isLoading && !snippet) {
    return (
      <Drawer
        isOpen={!!queryParams.selectedSnippetId}
        onClose={onClose}
      >
        <FallbackImage
          src="no-results"
          alt="Snippet not found"
          hasPadding
        >
          Snippet Not Found
        </FallbackImage>
      </Drawer>
    );
  }

  return (
    <Drawer
      isOpen={!!queryParams.selectedSnippetId}
      onClose={onClose}
    >
      {isLoading ? <Loading /> : (
        <>
          <Header snippetLocator={snippet?.id ?? ''} pathsCount={snippet?.matches.length ?? 0} />
          <Buttons onRejectClick={_.noop} onDownloadClick={_.noop} />
          <Tabs>
            {tabs.map(tab => (
              <Tabs.Tab
                key={tab.label}
                // eslint-disable-next-line react/jsx-props-no-spreading -- Is fine, no?
                {...tab}
              />
            ))}
          </Tabs>
          {activeTab === 'Details' ? (
            <Details />
          ) : (
            <Code />
          )}
          {/*
          TODO: gonna leave this in while building as it's helpful for debugging.
          <pre>
            {JSON.stringify(snippet, null, 2)}
          </pre> */}
        </>
      )}
    </Drawer>
  );
};

export default SnippetsDrawer;
